import { Party, ThreadPriority, ThreadStatus, ThreadView } from "./types";

// Labels and option lists for the Conversations tab, kept in one place so the
// list, the header controls and the lanes always use the same wording.

export const STATUS_LABEL: Record<ThreadStatus, string> = {
  open: "Open",
  waitingOnThirdParty: "Waiting on 3rd Party",
  waitingOnPod: "Waiting on POD",
  resolved: "Resolved",
};

export const STATUS_OPTIONS: ThreadStatus[] = [
  "open",
  "waitingOnThirdParty",
  "waitingOnPod",
  "resolved",
];

/** Mock data only; live tickets use only the priorities configured in System Configuration. */
export const PRIORITY_LABEL: Record<ThreadPriority, string> = {
  low: "Low",
  normal: "Medium",
  high: "High",
  urgent: "Urgent",
};

export const PRIORITY_OPTIONS: ThreadPriority[] = ["low", "normal", "high", "urgent"];

// Queues are not listed here: they are the mail service's ticket groups and
// arrive with the conversations (ConversationsSnapshot.ticketGroups).

/** Per-chain wording and the extra action each chain's emails offer. */
export const LANE_CONFIG: Record<
  Party,
  {
    label: string;
    /** "Send to customer" / "Send to agent". */
    sendLabel: string;
    /** Action beyond reply/forward/move — see MessageItem. */
    extraAction: "split" | "relay";
  }
> = {
  customer: { label: "Customer", sendLabel: "Send to Customer", extraAction: "split" },
  agent: { label: "Agent", sendLabel: "Send to Agent", extraAction: "relay" },
  // LTL + Final Mile orders: the LTL carrier's own chain beside the Final Mile agent's (KAN-1323)
  agentLtl: { label: "LTL Carrier", sendLabel: "Send to LTL Carrier", extraAction: "relay" },
  // LTL / LTL + Final Mile orders: the origin contact (shipper) the booking mail went to
  origin: { label: "Origin", sendLabel: "Send to Origin", extraAction: "relay" },
};

/** Who a chain is with, in running text ("is replying to the LTL carrier"). */
export const PARTY_NAME: Record<Party, string> = {
  customer: "customer",
  agent: "agent",
  agentLtl: "LTL carrier",
  origin: "origin contact",
};

/** Where "Relay" sends an email: agent mail goes to the customer, customer mail to the agent. */
export const otherParty = (party: Party): Party =>
  party === "customer" ? "agent" : "customer";

/** The agent lanes a thread has: the LTL carrier's only on LTL + Final Mile orders. */
export const agentParties = (hasLtlLane: boolean): Party[] =>
  hasLtlLane ? ["agent", "agentLtl"] : ["agent"];

/**
 * "agentLtl" is offered only on LTL + Final Mile threads, "origin" only on threads with origin-contact mail
 * (see ThreadWorkspace).
 */
export const VIEW_OPTIONS: { id: ThreadView; label: string }[] = [
  { id: "sideBySide", label: "Side by side" },
  { id: "customer", label: "Customer" },
  { id: "origin", label: "Origin" },
  { id: "agent", label: "Agent" },
  { id: "agentLtl", label: "LTL Carrier" },
  { id: "internal", label: "Internal notes" },
  { id: "activity", label: "Activity" },
];

/** How long the loader shows when a thread opens. */
export const OPEN_THREAD_LOADER_MS = 1000;
